import type { Role } from '../permissions/types.js';
import { inAudience, type AudiencePolicy } from '../audience/index.js';

// APP-104: in-app feedback. Pure rules only; storage, routes and screens live elsewhere.

export const FEEDBACK_KINDS = ['problem', 'idea', 'observation'] as const;
export type FeedbackKind = (typeof FEEDBACK_KINDS)[number];
export const TRIAGE_STATUSES = [
  'new',
  'in_review',
  'planned',
  'done',
  'wont_do',
  'duplicate',
] as const;
export type TriageStatus = (typeof TRIAGE_STATUSES)[number];

/**
 * What the sender sees of a report's status (APP-138 "Sender-facing words"): five words, never a
 * reviewer's. A duplicate follows its original, so the sender hears when it's fixed.
 */
export const SENDER_STATUSES = ['received', 'in_review', 'planned', 'done', 'not_planned'] as const;
export type SenderStatus = (typeof SENDER_STATUSES)[number];

export const SENDER_STATUS_WORDS: Record<SenderStatus, string> = {
  received: 'Received',
  in_review: 'In review',
  planned: 'Planned',
  done: 'Done',
  not_planned: 'Not planned',
};

const SENDER_STATUS_OF: Record<Exclude<TriageStatus, 'duplicate'>, SenderStatus> = {
  new: 'received',
  in_review: 'in_review',
  planned: 'planned',
  done: 'done',
  wont_do: 'not_planned',
};

/**
 * The sender's word for a triage status. `original` is the triage status of the report this one
 * duplicates (null when it isn't a duplicate, or the link is gone). A duplicate takes its
 * original's word; one whose original is missing, or is itself a duplicate, reads Received.
 */
export function senderStatus(triage: TriageStatus, original: TriageStatus | null): SenderStatus {
  if (triage !== 'duplicate') return SENDER_STATUS_OF[triage];
  return original === null || original === 'duplicate' ? 'received' : SENDER_STATUS_OF[original];
}

/** Open is Received, In review or Planned; Done and Not planned are closed. */
export function senderOpen(status: SenderStatus): boolean {
  return status === 'received' || status === 'in_review' || status === 'planned';
}

/** A report's latest news, as My feedback lists it: sent, a status change, or a reply. */
export interface SenderLatest {
  readonly kind: 'sent' | 'status' | 'team_reply' | 'your_reply';
  readonly status?: SenderStatus | undefined;
}

/**
 * The Latest words, the same in My feedback and the assistant's answers (APP-138 §D7): "You sent
 * it", "Now Planned", "The MyRewards team replied", "You replied".
 */
export function senderLatestWords(latest: SenderLatest): string {
  switch (latest.kind) {
    case 'sent':
      return 'You sent it';
    case 'status':
      return latest.status === undefined
        ? 'Its status changed'
        : `Now ${SENDER_STATUS_WORDS[latest.status]}`;
    case 'team_reply':
      return 'The MyRewards team replied';
    case 'your_reply':
      return 'You replied';
  }
}

/**
 * A status change the sender can see changes their word (APP-138 §D3, §D4). Marking an In review
 * report a duplicate of another In review report changes nothing for them.
 */
export function senderVisibleChange(before: SenderStatus, after: SenderStatus): boolean {
  return before !== after;
}
/**
 * Where a feedback report's changes are kept in the audit trail (APP-104 §9, one format): its
 * entity type, and one field per triage field. The trail already holds these names, and a
 * report's status history is read back by them, so the writers and the reads share these.
 */
export const FEEDBACK_ENTITY = 'feedback_report';
export const FEEDBACK_TRIAGE_FIELDS = {
  status: 'Triage status',
  kind: 'Kind',
  priority: 'Priority',
  duplicateOf: 'Duplicate of',
} as const;

export const SCREENSHOT_STATES = [
  'pending_upload',
  'attached',
  'excluded',
  'unavailable',
  'omitted',
] as const;
export type ScreenshotState = (typeof SCREENSHOT_STATES)[number];

/** Why a capture failed in the browser; the words shown to a reviewer. */
export const CAPTURE_REASONS = {
  too_large: 'The screenshot was too large to attach.',
  unreadable: 'The screenshot couldn’t be read.',
  too_many_pixels: 'The screenshot was larger than the supported image size.',
  failed: 'The screenshot couldn’t be taken.',
} as const;
/** Why an upload ended as omitted; the words shown to a reviewer. */
export const OMITTED_REASONS = {
  upload_failed: 'The screenshot couldn’t be uploaded because the upload didn’t finish.',
  upload_refused: 'The screenshot couldn’t be uploaded because it was refused.',
  expired: 'The screenshot didn’t finish uploading within 5 minutes of being sent.',
} as const;

export const FEEDBACK_LIMITS = {
  wordsChars: 16_000,
  diagnosticsBytes: 65_536,
  screenshotBytes: 5_242_880,
  screenshotPixels: 16_000_000,
  ringRequests: 50,
  ringErrors: 20,
  ringPages: 20,
  errorMessageChars: 300,
  stackFrames: 10,
  noteChars: 4_000,
  draftsOpenedPerHour: 30,
  openDrafts: 10,
  reportsPerDay: 20,
  attachWindowMs: 300_000,
  draftKeepMs: 86_400_000,
  titleChars: 80,
  // APP-138 (replies): the sender's reply on every channel, and the team's.
  replyChars: 4_000,
  teamReplyChars: 8_000,
  /** The sender's replies per organization day, across MyRewards, email and Teams. */
  repliesPerDay: 30,
  /** Contact presses (Email in Outlook, Chat in Teams) per reviewer per hour. */
  contactsPerHour: 60,
  /** The model's short "about" phrase for a typed reply it reads back. */
  aboutChars: 80,
  /** The most open reports a "which feedback?" chooser offers. */
  chooserSize: 10,
} as const;

/**
 * Characters no one types into a reply, as every reply files them (APP-138 SENT-023): the C0
 * controls but line breaks and tabs (a NUL can't be stored as text), DEL, the C1 controls, and
 * the bidi embeddings, overrides and isolates, which would make the words read in another order
 * in the bell, the email and the Teams card.
 */
// eslint-disable-next-line no-control-regex -- removing control characters is the point.
const CONTROL = /[\u0000-\u0008\u000b-\u001f\u007f-\u009f\u202a-\u202e\u2066-\u2069]/g;

/**
 * A reply's words as they're filed, on every channel and from either side (the sender's in
 * MyRewards, by email or from a Teams card, and the team's): without those characters, trimmed.
 */
export function withoutControls(text: string): string {
  return text.replace(CONTROL, '').trim();
}

/** Format characters and the characters Unicode says to show as nothing (U+FE0F, U+3164…). */
const HIDDEN = /[\p{Cf}\p{Default_Ignorable_Code_Point}]/gu;

/**
 * Words someone outside wrote, as a reader would see them (APP-209, SENT-095): without the
 * controls `withoutControls` removes, nor any format or default-ignorable character (soft
 * hyphens, zero-width and bidi characters, variation selectors, fillers), which show as nothing
 * but can split a link or an address so a check misses it. Trimmed.
 */
export function withoutHidden(text: string): string {
  return withoutControls(text.replace(HIDDEN, ''));
}

/**
 * Someone's words cut to at most `n` UTF-16 units, never through a character (APP-209, SENT-097):
 * a cut that would leave the first half of a pair drops it, and any half already alone becomes
 * U+FFFD. A text column takes half a pair, but jsonb (the audit, a message's payload) refuses it,
 * so one emoji at the cut could stop a whole receive.
 */
export function cutText(text: string, n: number): string {
  return text
    .slice(0, n)
    .replace(/[\uD800-\uDBFF]$/, '')
    .toWellFormed();
}

/**
 * Voice for feedback (APP-104 §8). The audio is PCM, 16 kHz, 16-bit, mono. INFRA-016 measured no
 * cut for a busy socket on the gateway or the ingress, so a recording runs the full 5 minutes;
 * what still cuts is silence, so a session with no audio closes long before the gateway would.
 */
export const FEEDBACK_VOICE_LIMITS = {
  /** One recording at most: 5 minutes of audio. At the cap it finishes as if Done were pressed. */
  recordingMs: 300_000,
  /** Voice time per person per organization day (30 minutes). */
  dailySeconds: 1_800,
  /** The shortest session admitted: with less of the day left, voice reads as used up. */
  minSessionSeconds: 5,
  /** Live sessions per person at once; a third is refused. */
  liveSessions: 2,
  /** Live sessions one server process runs at once; beyond it a start is refused as busy. */
  serverSessions: 50,
  /** This long of audio below the silence line ends the session. */
  silenceMs: 15_000,
  /** This long with no audio frames at all ends the session. */
  noAudioMs: 15_000,
  /** The largest audio frame accepted (the browser sends ~3,200-byte frames of 100 ms). */
  frameBytes: 32_768,
  sampleRate: 16_000,
  bitsPerSample: 16,
  channels: 1,
  /** 16,000 samples a second, two bytes each. */
  bytesPerSecond: 32_000,
  /** The first message, `start` with the ticket, must arrive within this long of the upgrade. */
  startMessageMs: 10_000,
  /** A voice ticket works once, within this long of opening the draft. */
  ticketMs: 120_000,
  /** Random bytes in a ticket (sent as base64url, stored only as a SHA-256). */
  ticketBytes: 32,
  /** Audio queued for the speech service beyond this ends the session (the connection is slow). */
  queuedAudioMs: 5_000,
} as const;

/**
 * Help chat's dictation (APP-124) shares every limit above, but one recording stops at 2 minutes:
 * a spoken question (about 300 words) then fits the question box's 2,000 characters.
 */
export const HELP_VOICE_RECORDING_MS = 120_000;

/**
 * Help chat's quiet window (APP-174): once the first words are heard, this long below the silence
 * line ends the recording, as if Done were pressed. A spoken question is one or two sentences, and
 * a speaker's pauses inside one are well under this; 15 s kept recording long after the question
 * ended (AIE-025). Until the first words, feedback's `silenceMs` holds, so nobody is cut off while
 * they gather their thoughts. Feedback keeps its own window.
 */
export const HELP_VOICE_QUIET_MS = 3_000;

type Who = { personId: string; roles: readonly Role[] };

/** The master switch and the audience, both. The server checks this on every feedback call. */
export function mayReport(s: { enabled: boolean; audience: AudiencePolicy }, who: Who): boolean {
  return s.enabled && inAudience(s.audience, who);
}

/** '/a?b#c' becomes '/a'. */
export function stripQuery(path: string): string {
  const end = path.search(/[?#]/);
  return end === -1 ? path : path.slice(0, end);
}

/** Emails become [email], runs of 6 or more digits become [number], query strings go, 300 at most. */
export function redactMessage(text: string): string {
  // Cut first: the patterns below backtrack badly on very long tokens, so they only ever see a
  // bounded slice. Redaction can only shorten, so 4x the limit leaves room for the final cut.
  return text
    .slice(0, FEEDBACK_LIMITS.errorMessageChars * 4)
    .replace(/[^\s@<>()[\]]+@[^\s@<>()[\]]+\.[^\s@<>()[\]]+/g, '[email]')
    .replace(/\?\S+/g, '')
    .replace(/\d{6,}/g, '[number]')
    .slice(0, FEEDBACK_LIMITS.errorMessageChars);
}

/** One line of at most 80 characters: whitespace and newlines collapse to single spaces. */
const oneLine = (text: string, max: number): string =>
  text.replace(/\s+/g, ' ').trim().slice(0, max);

/** The summary's title only when it was made for exactly these words; otherwise the first line. */
export function deriveTitle(
  words: string,
  summary: { title: string; wordsHash: string } | null,
  wordsHash: string,
): string {
  if (summary !== null && summary.wordsHash === wordsHash)
    return oneLine(summary.title, FEEDBACK_LIMITS.titleChars);
  const first = words.split(/\r?\n/).find((line) => line.trim() !== '') ?? '';
  return oneLine(first, FEEDBACK_LIMITS.titleChars);
}

/** A pending upload that is past its window becomes omitted (expired). Anything else stays. */
export function settleScreenshot(
  state: ScreenshotState,
  attachUntil: Date | null,
  now: Date,
): { state: ScreenshotState; reason?: keyof typeof OMITTED_REASONS } {
  if (state === 'pending_upload' && attachUntil !== null && now.getTime() > attachUntil.getTime())
    return { state: 'omitted', reason: 'expired' };
  return { state };
}

/** A backtick fence one longer than the longest run in the text, and at least three. */
export function fenceFor(text: string): string {
  const longest = Math.max(0, ...(text.match(/`+/g) ?? []).map((run) => run.length));
  return '`'.repeat(Math.max(3, longest + 1));
}

/** One voice session's words as heard (APP-104 §6, §8): never audio. */
export interface SpokenSession {
  status: 'saved' | 'interrupted';
  provider: 'azure_speech' | 'stand_in';
  startedAt: string;
  audioMs: number;
  phrases: readonly { text: string; offsetMs: number }[];
}

/** The words of every session, a paragraph each; '' when no words were heard. */
export function spokenText(sessions: readonly SpokenSession[] | null): string {
  return (sessions ?? [])
    .map((s) =>
      s.phrases
        .map((p) => p.text.trim())
        .filter((t) => t !== '')
        .join(' '),
    )
    .filter((t) => t !== '')
    .join('\n\n');
}

export interface FeedbackExport {
  displayId: string;
  title: string;
  /** Null until someone sorts it: the reporter may leave it unchosen (APP-104 §3.3). */
  kind: FeedbackKind | null;
  status: TriageStatus;
  priority: string | null;
  duplicateOf: string | null;
  words: string;
  /** What was said, session by session (saved or interrupted), in order; null when nothing was. */
  spoken: readonly SpokenSession[] | null;
  input: 'typed' | 'spoken' | 'both';
  sentAt: string;
  from: {
    name: string;
    employeeNumber: string;
    roles: readonly string[];
    previewingAs: string | null;
  };
  page: { path: string; title: string; area: string };
  browser: string;
  appVersion: string;
  /**
   * The screenshot's state: `attached` only when the picture goes with it. A deleted or purged
   * one says so (with `screenshotGone`), never "omitted".
   */
  screenshot: ScreenshotState | 'deleted' | 'purged';
  /** What happened to a screenshot that's gone; null for every other state. */
  screenshotGone: GoneScreenshot | null;
  /** The part of the picture the person had on screen, in picture pixels (QA-059). */
  screenshotWindow: { x: number; y: number; width: number; height: number } | null;
}

/**
 * A screenshot that's gone, as report.md and report.json say it: a System Admin deleted it (who,
 * and the organization's date, `YYYY-MM-DD`, each null when not known), or the retention job
 * removed it after so many days (null when the day it went isn't known).
 */
export type GoneScreenshot =
  | { readonly kind: 'deleted'; readonly by: string | null; readonly on: string | null }
  | { readonly kind: 'purged'; readonly afterDays: number | null };

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];

/** `2026-10-21` as "Oct 21, 2026", as the app shows dates. */
function dayWords(date: string): string {
  const [year, month, day] = date.split('-').map(Number);
  const name = MONTHS[(month ?? 0) - 1];
  return name === undefined || day === undefined || year === undefined
    ? date
    : `${name} ${String(day)}, ${String(year)}`;
}

/**
 * "Screenshot deleted by ⟨name⟩ on ⟨date⟩" or "Screenshot removed after N days". `name` shapes
 * the person's name (report.md makes it inert code).
 */
export function goneScreenshotWords(
  gone: GoneScreenshot,
  name: (value: string) => string = (value) => value,
): string {
  if (gone.kind === 'deleted')
    return `Screenshot deleted by ${gone.by === null ? 'a System Admin' : name(gone.by)}${
      gone.on === null ? '' : ` on ${dayWords(gone.on)}`
    }`;
  return gone.afterDays === null
    ? 'Screenshot removed after its keep time'
    : `Screenshot removed after ${String(gone.afterDays)} ${gone.afterDays === 1 ? 'day' : 'days'}`;
}

const KIND_WORDS: Record<FeedbackKind, string> = {
  problem: 'Problem',
  idea: 'Idea',
  observation: 'Observation',
};
const STATUS_WORDS: Record<TriageStatus, string> = {
  new: 'New',
  in_review: 'In review',
  planned: 'Planned',
  done: 'Done',
  wont_do: 'Won’t do',
  duplicate: 'Duplicate',
};
const INPUT_WORDS = { typed: 'Typed', spoken: 'Spoken', both: 'Typed and spoken' } as const;
const ROLE_WORDS: Record<string, string> = {
  paa_taa_approver: 'PAA/TAA approver',
  spot_reviewer: 'Spot Committee member',
};
const roleWord = (role: string): string =>
  ROLE_WORDS[role] ??
  role
    .split('_')
    .map((w, n) => (n === 0 ? w.charAt(0).toUpperCase() + w.slice(1) : w))
    .join(' ');

/** A client- or reporter-controlled value as inline code: one line, with a delimiter no backtick run inside can close. */
function code(value: string): string {
  const text = value.replace(/\s+/g, ' ').trim();
  if (text === '') return '(blank)';
  const fence = '`'.repeat(Math.max(0, ...(text.match(/`+/g) ?? []).map((run) => run.length)) + 1);
  const pad = text.startsWith('`') || text.endsWith('`') ? ' ' : '';
  return `${fence}${pad}${text}${pad}${fence}`;
}
/**
 * report.md (APP-104 §6): GitLab-ready, with the words in a fence no backtick run can close.
 * Every value a reporter (or the summary their words steer) controls is inline code or fenced,
 * the title included (SENT-009): GitLab resolves no `@` mention (`@all` too), `#123`, `!123`,
 * `%milestone` or `$snippet` reference, and autolinks no URL, inside code, and a pasted report
 * must never ping people or plant a link. Backslash escapes would leave `@`, `%`, `$` and bare
 * URLs live; a code span reads as plain text, keeps every character as typed, and is the shape
 * the Facts lines already use.
 */
export function feedbackMarkdown(r: FeedbackExport): string {
  const fence = fenceFor(r.words);
  const lines = [
    `# ${code(r.displayId)}: ${code(r.title)}`,
    '',
    `Kind: ${r.kind === null ? 'Not sorted yet' : KIND_WORDS[r.kind]} · Status: ${STATUS_WORDS[r.status]}${
      r.priority ? ` · Priority: ${code(r.priority)}` : ''
    }${r.duplicateOf ? ` · Duplicate of ${code(r.duplicateOf)}` : ''}`,
    '',
    '## Their words',
    '',
    fence,
    r.words,
    fence,
  ];
  const spoken = spokenText(r.spoken);
  if (spoken !== '' && spoken !== r.words) {
    const spokenFence = fenceFor(spoken);
    lines.push('', 'As spoken:', '', spokenFence, spoken, spokenFence);
  }
  lines.push(
    '',
    '## Facts',
    '',
    `- From: ${code(r.from.name)} (${code(r.from.employeeNumber)}), ${r.from.roles
      .map((role) => code(roleWord(role)))
      .join(', ')}${r.from.previewingAs ? `, previewing as ${code(r.from.previewingAs)}` : ''}`,
    `- Sent: ${code(r.sentAt)}`,
    `- Page: ${code(r.page.title)} (${code(r.page.area)}) ${code(r.page.path)}`,
    `- Browser: ${code(r.browser)}`,
    `- App version: ${code(r.appVersion)}`,
    `- Input: ${INPUT_WORDS[r.input]}`,
  );
  if (r.screenshot === 'attached')
    lines.push('', '## Screenshot', '', '![Screenshot](screenshot.png)');
  else if (r.screenshotGone !== null)
    lines.push('', '## Screenshot', '', goneScreenshotWords(r.screenshotGone, code));
  return `${lines.join('\n')}\n`;
}
